// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Hidden from "@insite/mobius/Hidden";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { constants } from "@keller/XDL/constants";
import { avoidBreakInsidePrintElement, hideElementOnPrint, xClassNames, xScreen } from "@keller/XDL/Utilities";
import { mergeWithCss } from "@keller/XDL/Utilities/mergeWithCss";
import React, { Children, FC, PropsWithChildren, useState } from "react";
import { css } from "styled-components";

export type CartLineContainerProps = {
    extendedStyles?: CartLineContainerStyles;
    mobileExtendedStyles?: MobileCartLineContainerStyles;
    orderLineStyle?: boolean;
    [key: string]: any;
};

interface OwnProps {
    Image: FC<PropsWithChildren>;
    Info: FC<PropsWithChildren>;
    PeripheralActionsLeft: FC<PropsWithChildren>;
    PeripheralAvailabilityAction: FC<PropsWithChildren>;
    PeripheralRemoveAction: FC<PropsWithChildren>;
    Qty: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Total: FC<PropsWithChildren>;
}

type Props = React.FC<PropsWithChildren<CartLineContainerProps>> & OwnProps;

export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    mainWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    infoSection?: InjectableCss;
    qtyPriceSection?: InjectableCss;
    qty?: InjectableCss;
    price?: InjectableCss;
    total?: InjectableCss;
    peripheralActions?: InjectableCss;
    peripheralActionsLeft?: InjectableCss;
    peripheralActionsRight?: InjectableCss;
}

export const cartLineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui5")};
            padding: 24px 12px;
            display: flex;

            &.first-item {
                padding: 12px 12px 24px;
            }

            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                &,
                & > * {
                    display: none;
                }
            }
            @media print {
                display: none;
            }
            ${avoidBreakInsidePrintElement}
        `,
    },
    image: {
        css: css`
            width: 88px;
            height: 88px;
            margin-right: 22px;

            ${hideElementOnPrint}
        `,
    },
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 440px;
            padding-right: 20px;

            &.orderLineStyle {
                width: 700px;
            }

            &,
            &.orderLineStyle {
                ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                    width: 100%;
                    padding: 0;
                    margin-bottom: 15px;
                }
            }
        `,
    },
    qtyPriceSection: {
        css: css`
            display: flex;
            flex-grow: 1;
        `,
    },
    qty: {
        css: css`
            flex: 1;
        `,
    },
    price: {
        css: css`
            flex: 1;
            text-align: right;
        `,
    },
    total: {
        css: css`
            flex: 1;
            text-align: right;
        `,
    },
    peripheralActions: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    peripheralActionsLeft: {
        css: css`
            &,
            & > span {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 64px;
                flex: 1;
            }
        `,
    },
    peripheralActionsRight: {
        css: css`
            max-width: 394px;
            &,
            & > span {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex: 1;
                gap: 90px;
                ${xScreen("lte", "xs")} {
                    align-items: flex-end;
                }
            }
        `,
    },
};

export interface MobileCartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    upperSection?: InjectableCss;
    mainWrapper?: InjectableCss;
    total?: InjectableCss;
    qty?: InjectableCss;
    lowerSection?: InjectableCss;
    qtyPriceSection?: InjectableCss;
    peripheralActions?: InjectableCss;
    peripheralActionsLeft?: InjectableCss;
    peripheralActionsRight?: InjectableCss;
    peripheralActionsOnPrint?: InjectableCss;
    peripheralActionsLeftOnPrint?: InjectableCss;
}

export const mobileCartLineContainerStyles: MobileCartLineContainerStyles = {
    container: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui5")};
            padding: 18px 0 20px;
            display: flex;
            flex-direction: column;

            &.first-item {
                padding: 20px 0 12px;
                @media print {
                    padding: 4px 0 12px;
                }
            }

            ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                &,
                & > * {
                    display: none;
                }
            }

            @media print {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                padding: 13px 0 12px;
                flex-wrap: wrap;
                gap: 0 10px;
            }
            ${avoidBreakInsidePrintElement}
        `,
    },
    upperSection: {
        css: css`
            display: flex;
            gap: 12px;

            @media print {
                width: 100%;
                flex: 1 1 calc(50% - 5px);
                max-width: calc(50% - 5px);
            }
        `,
    },
    image: {
        css: css`
            width: 88px;
            height: 88px;

            ${hideElementOnPrint}
        `,
    },
    mainWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        `,
    },
    lowerSection: {
        css: css`
            margin-top: 12px;
            display: flex;
            flex-direction: column;
            flex-grow: 1;

            @media print {
                flex: 1 1 calc(50% - 5px);
                max-width: calc(50% - 5px);
                flex-direction: row;
                margin-top: 0px;
                width: 360px;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            line-height: 0;
        `,
    },
    total: {
        css: css`
            flex: 1;
            text-align: right;
            line-height: 0;
            @media print {
                min-width: 80px;
            }
        `,
    },
    qtyPriceSection: {
        css: css`
            margin-top: 16px;
            display: flex;
            justify-content: space-between;
            flex-grow: 1;

            @media print {
                margin-top: 0;
                padding-left: 12px;
                gap: 10px;
                width: calc(100% - 120px);
            }
        `,
    },
    peripheralActions: {
        css: css`
            margin-top: 16px;
            display: flex;
            justify-content: space-between;
            flex-grow: 1;
            align-items: flex-end;

            ${hideElementOnPrint}
        `,
    },
    peripheralActionsLeft: {
        css: css`
            &,
            & > span {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 16px;
                flex: 1;
            }
        `,
    },
    peripheralActionsRight: {
        css: css`
            &,
            & > span {
                display: flex;
                justify-content: flex-end;
                flex: 1;
                gap: 16px;
                align-items: flex-end;
            }
        `,
    },
    peripheralActionsOnPrint: {
        css: css`
            display: none;
            @media print {
                display: block;
                margin-top: 8px;
            }
        `,
    },
    peripheralActionsLeftOnPrint: {
        css: css`
            div {
                display: flex;
            }
        `,
    },
};

const Image: FC = () => null;
const Info: FC = () => null;
const PeripheralActionsLeft: FC = () => null;
const PeripheralAvailabilityAction: FC = () => null;
const PeripheralRemoveAction: FC = () => null;
const Qty: FC = () => null;
const Price: FC = () => null;
const Total: FC = () => null;

const CartLineContainer: Props = ({
    children,
    extendedStyles,
    mobileExtendedStyles,
    orderLineStyle,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(cartLineContainerStyles, extendedStyles));
    const [mobileStyles] = useState(() => mergeWithCss(mobileCartLineContainerStyles, mobileExtendedStyles));

    const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
    const imageSlot = childrenArray.find(child => child.type === Image);
    const infoSlot = childrenArray.find(child => child.type === Info);
    const peripheralActionsLeft = childrenArray.find(child => child.type === PeripheralActionsLeft);
    const peripheralAvailabilityAction = childrenArray.find(child => child.type === PeripheralAvailabilityAction);
    const peripheralRemoveAction = childrenArray.find(child => child.type === PeripheralRemoveAction);
    const qtySlot = childrenArray.find(child => child.type === Qty);
    const priceSlot = childrenArray.find(child => child.type === Price);
    const totalSlot = childrenArray.find(child => child.type === Total);

    const orderLineStyleClassName = orderLineStyle ? "orderLineStyle" : "";
    const className = xClassNames.append(orderLineStyleClassName, otherProps.className);
    const mobileClassName = xClassNames.append(className, "mobile-line");

    return (
        <>
            {/* Mobile styles */}
            <StyledWrapper {...mobileStyles.container} {...otherProps} className={mobileClassName}>
                <StyledWrapper {...mobileStyles.upperSection}>
                    <StyledWrapper {...mobileStyles.image}>
                        {imageSlot?.props?.children || <div style={{ lineHeight: 0 }}></div>}
                    </StyledWrapper>
                    <StyledWrapper {...mobileStyles.mainWrapper}>
                        {infoSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...mobileStyles.lowerSection}>
                    {priceSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                    {peripheralAvailabilityAction?.props?.children || <div style={{ lineHeight: 0 }}></div>}
                    <StyledWrapper {...mobileStyles.qtyPriceSection}>
                        <StyledWrapper {...mobileStyles.qty}>
                            {qtySlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                        </StyledWrapper>
                        <StyledWrapper {...mobileStyles.total}>
                            {totalSlot?.props.children || <div style={{ lineHeight: 0 }}></div>}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...mobileStyles.peripheralActions} data-test-selector="peripheralActions">
                        <StyledWrapper
                            {...mobileStyles.peripheralActionsLeft}
                            data-test-selector="peripheralActionsLeft"
                        >
                            {peripheralActionsLeft?.props?.children ? (
                                <span style={{ lineHeight: 0 }}>{peripheralActionsLeft?.props?.children || <></>}</span>
                            ) : (
                                <div style={{ lineHeight: 0 }}></div>
                            )}
                        </StyledWrapper>
                        <StyledWrapper
                            {...mobileStyles.peripheralActionsRight}
                            data-test-selector="peripheralActionsRight"
                        >
                            {peripheralRemoveAction?.props?.children ? (
                                <span style={{ lineHeight: 0 }}>
                                    {peripheralRemoveAction?.props?.children || <></>}
                                </span>
                            ) : (
                                <div style={{ lineHeight: 0 }}></div>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...mobileStyles.peripheralActionsOnPrint} data-test-selector="peripheralActionsmobile">
                    <StyledWrapper
                        {...mobileStyles.peripheralActionsLeftOnPrint}
                        data-test-selector="peripheralActionsLeft"
                    >
                        <StyledWrapper as="span" style={{ lineHeight: 0 }}>
                            {peripheralActionsLeft?.props?.children || <></>}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {/* Desktop styles */}
            <StyledWrapper {...styles.container} {...otherProps} className={className}>
                <StyledWrapper {...styles.image}>{imageSlot?.props?.children}</StyledWrapper>
                <StyledWrapper {...styles.mainWrapper}>
                    <StyledWrapper {...styles.infoWrapper}>
                        <StyledWrapper {...styles.infoSection} className={orderLineStyleClassName}>
                            {infoSlot?.props.children}
                        </StyledWrapper>
                        <StyledWrapper {...styles.qtyPriceSection}>
                            <StyledWrapper {...styles.qty}>{qtySlot?.props.children}</StyledWrapper>
                            <StyledWrapper {...styles.price}>{priceSlot?.props.children}</StyledWrapper>
                            <StyledWrapper {...styles.total}>{totalSlot?.props.children}</StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.peripheralActions} data-test-selector="peripheralActions">
                        <StyledWrapper {...styles.peripheralActionsLeft} data-test-selector="peripheralActionsLeft">
                            <span style={{ lineHeight: 0 }}>{peripheralActionsLeft?.props?.children}</span>
                        </StyledWrapper>
                        <StyledWrapper {...styles.peripheralActionsRight} data-test-selector="peripheralActionsRight">
                            <span style={{ lineHeight: 0 }}>
                                <>{peripheralAvailabilityAction?.props?.children || <></>}</>
                                <>{peripheralRemoveAction?.props?.children || <></>}</>
                            </span>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Info = Info;
CartLineContainer.Qty = Qty;
CartLineContainer.Price = Price;
CartLineContainer.Total = Total;
CartLineContainer.PeripheralActionsLeft = PeripheralActionsLeft;
CartLineContainer.PeripheralAvailabilityAction = PeripheralAvailabilityAction;
CartLineContainer.PeripheralRemoveAction = PeripheralRemoveAction;

export interface LineHeaderStyles {
    wrapper?: InjectableCss;
    itemDescription?: InjectableCss;
    rightSection?: InjectableCss;
    qty?: InjectableCss;
    priceUnit?: InjectableCss;
    total?: InjectableCss;
}

export const lineHeaderStyles: LineHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            padding: 8px 12px 8px;
            background: ${getColor("common.ui12")};
            font-size: 14px;
            line-height: 19px;
            font-weight: bold;
            width: 100%;
            ${hideElementOnPrint}
        `,
    },
    itemDescription: {
        css: css`
            width: 550px;
            &.orderLineStyle {
                width: 810px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                width: 100%;
                & > ${TypographyStyle} {
                    display: none;
                }
            }
        `,
    },
    rightSection: {
        css: css`
            flex-grow: 1;
            display: flex;

            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                display: none;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            padding-left: 5px;
        `,
    },
    priceUnit: {
        css: css`
            flex: 1;
            text-align: center;
        `,
    },
    total: {
        css: css`
            flex: 1;
            text-align: right;
        `,
    },
};

interface LinesHeaderProps {
    leftTitle?: string;
    rightTitles?: string[];
    extendedStyles?: LineHeaderStyles;
    orderLineStyle?: boolean;
}

export const LinesHeader: FC<LinesHeaderProps> = ({
    leftTitle = "Item Description",
    rightTitles = ["QTY", "Price/Unit", "Total"],
    orderLineStyle,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(lineHeaderStyles, extendedStyles));
    const orderLineStyleClassName = orderLineStyle ? "orderLineStyle" : "";

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.itemDescription} className={orderLineStyleClassName}>
                <Typography>{translate(leftTitle || "")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSection}>
                <StyledWrapper {...styles.qty}>{translate(rightTitles?.[0] || "")}</StyledWrapper>
                <StyledWrapper {...styles.priceUnit}>{translate(rightTitles?.[1] || "")}</StyledWrapper>
                {rightTitles?.[2] && (
                    <StyledWrapper {...styles.total}>{translate(rightTitles?.[2] || "")}</StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default CartLineContainer;
